import React, { useState, useRef, useEffect } from 'react';
import { authPost } from '../lib/authFetch';
import './UniversalChat.css';

function UniversalChat({
  dataMode,
  onNavigate,
  onAction,
  conversationHistory,
  onAddMessage,
  autoFocus = false,
  alwaysShowSubmit = false,
  hidePreview = false
}) {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const inputRef = useRef(null);

  const placeholderExamples = [
    "I just met a new donor named John Smith...",
    "Show me all donors ranked by amount...",
    "Move Sarah to the conversation stage...",
    "Create a list grouped alphabetically...",
    "Add a note about my call with Michael...",
    "Who should I follow up with this week?",
    "Show me everyone in the ask stage...",
    "Group donors by their giving level..."
  ];

  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex(prev => (prev + 1) % placeholderExamples.length);
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-focus on mount if autoFocus is true
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);
    setStreamingText('');

    // Add user message to history
    onAddMessage({ role: 'user', content: userMessage });

    try {
      const response = await authPost('/api/universal-chat-stream', {
        message: userMessage,
        conversationHistory: conversationHistory.slice(-10),
        mode: dataMode
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';
      let finalData = null;
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // A single SSE event can be split across two reads by the underlying TCP
        // stream - buffer any incomplete trailing line instead of dropping it.
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'chunk') {
                // Accumulate streaming text and try to extract just the response part
                fullResponse += data.text;
                // Try to extract just the "response" text from JSON as it streams
                const responseMatch = fullResponse.match(/"response"\s*:\s*"([^"]*)"/);
                if (responseMatch) {
                  setStreamingText(responseMatch[1]);
                }
              } else if (data.type === 'done') {
                finalData = data;
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (parseError) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }

      // Clear streaming text and add final response
      setStreamingText('');

      if (finalData) {
        onAddMessage({
          role: 'assistant',
          content: finalData.response,
          action: finalData.action,
          timestamp: new Date().toISOString()
        });

        if (finalData.action) {
          // Pass the original user message along with the action
          onAction({ ...finalData.action, originalMessage: userMessage });
        }
      }

    } catch (error) {
      console.error('Chat error:', error);
      setStreamingText('');
      onAddMessage({
        role: 'assistant',
        content: "Sorry, I couldn't process that request. Please try again.",
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsExpanded(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className={`universal-chat ${isExpanded ? 'expanded' : ''} ${isLoading ? 'loading' : ''}`}>
      <form onSubmit={handleSubmit} className="universal-chat-form">
        <div className="universal-chat-input-wrapper">
          <svg className="chat-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={() => setIsExpanded(true)}
            onBlur={() => !input && setIsExpanded(false)}
            onKeyDown={handleKeyDown}
            placeholder={placeholderExamples[placeholderIndex]}
            className="universal-chat-input"
            disabled={isLoading}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-form-type="other"
          />
          {isLoading && (
            <div className="chat-loading">
              <div className="chat-loading-dot"></div>
              <div className="chat-loading-dot"></div>
              <div className="chat-loading-dot"></div>
            </div>
          )}
          {!isLoading && (
            <button type="submit" className="chat-submit">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </form>

      {/* Streaming response display */}
      {streamingText && (
        <div className="chat-streaming">
          <span className="chat-streaming-label">knowroom</span>
          <span className="chat-streaming-text">{streamingText}</span>
          <span className="chat-streaming-cursor">|</span>
        </div>
      )}

      {/* Recent response preview */}
      {!hidePreview && conversationHistory.length > 0 && isExpanded && !streamingText && (
        <div className="chat-preview">
          {conversationHistory.slice(-2).map((msg, i) => (
            <div key={i} className={`chat-preview-message ${msg.role}`}>
              <span className="chat-preview-role">{msg.role === 'user' ? 'You' : 'knowroom'}</span>
              <span className="chat-preview-content">{msg.content.substring(0, 100)}{msg.content.length > 100 ? '...' : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default UniversalChat;
